import axios from "axios";
import { getAuthToken, logout, getSourceName, getUserProfile } from "./authService";
import { getApiSourceName } from "../config/clientConfig";
import { getApiGeneration, assertV2Ok } from "./apiGeneration";
import { toUploads } from "./adapters/hegicUploads";
import { toLegacyAssessment } from "./adapters/hegicAssessment";

import {
  API_BASE_URL,
  getAnalyticsBaseUrl,
  getApiBaseUrl,
  getPortalOrigin,
  PORTAL_BASE_URL,
  withTrailingSlash,
} from "../config/urls";

// Re-exported so existing importers of apiService are unaffected by the move.
export { getApiGeneration, assertV2Ok };

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

// Phase 4: read api_base_url from portal config stored in localStorage.
// Returns null when no portal config is available (falls back to instance default).
const getDynamicBaseUrl = () => {
  try {
    const stored = localStorage.getItem("portalConfig");
    if (!stored) return null;
    const config = JSON.parse(stored);
    return config?.configuration?.api_base_url || null;
  } catch {
    return null;
  }
};

// Backend source_name sent to assessment API endpoints (e.g. "Bajaj_pp").
// Distinct from the portal-facing UI name ("bajaj") stored in localStorage.
// Priority: portal config > logged-in client's registry entry > build-time default.
/**
 * The source_name to send with a request.
 *
 * Delegates to clientConfig, which resolves the logged-in client's own entry
 * FIRST and only then the cached portalConfig record. This used to have its own
 * copy of the logic with those two the other way round, and Ergo's property
 * line made the difference matter: the cached record is fetched once at login
 * for the motor client, so switching to property kept sending motor's
 * source_name. These APIs answer a wrong source_name with an empty collection
 * rather than an error, so the property list would have come back empty and
 * read as "no claims".
 */
const getEffectiveSourceName = () =>
  getApiSourceName() || getSourceName() || getUserProfile()?.client_source_name || "";

// ---------------------------------------------------------------------------
// Axios instance
// ---------------------------------------------------------------------------

// Exported so other services (userService, etc.) can route all client-API
// calls through this interceptor rather than using raw axios.
export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    Referer: withTrailingSlash(PORTAL_BASE_URL),
    "User-Agent":
      "Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/140.0.0.0 Safari/537.36",
    Accept: "*/*",
    "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
  },
});

// Request interceptor: inject auth token + Phase 4 dynamic base URL
apiClient.interceptors.request.use(
  (config) => {
    const token = getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // NOTE: the Phase 4 baseURL override sourced from the *portal config API*
    // stays removed — it served a stale host (bagicpp.api.camcom.ai) and
    // silently rerouted production traffic.
    //
    // getApiBaseUrl() is not that: it resolves against the in-repo client map
    // in src/config/urls.js, so a client on its own deployment (Hegic) reaches
    // its own backend while every other client keeps the shared gateway.
    // config.apiHost lets ONE route go to a different host than the rest of the
    // client's API. Ergo motor needs it: /get_analytics_data 404s on the host
    // that serves its case details, and that host 404s on /assessment_new — see
    // getAnalyticsBaseUrl in src/config/urls.js. A request without it resolves
    // the client's normal host, so nothing else changes.
    config.baseURL = config.apiHost || getApiBaseUrl();

    // Every request, with the host it actually went to. A wrong host is the
    // failure this integration keeps producing and it is invisible otherwise:
    // the request succeeds against someone else's backend, or 404s on a route
    // that host has never had.
    if (process.env.NODE_ENV !== "production") {
      console.info(
        `[API] ${String(config.method || "get").toUpperCase()} ${config.baseURL}${config.url}`,
        { source_name: getEffectiveSourceName(), authenticated: Boolean(token) },
      );
    }

    return config;
  },
  (error) => Promise.reject(error),
);

// Response interceptor: handle token expiration
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    // Name the host and the route on the way out. A 404 here almost always
    // means the right route on the wrong host, or a dev-proxy path the running
    // server does not know about — and neither is legible from the status alone.
    if (process.env.NODE_ENV !== "production") {
      const { config, response } = error;
      const url = `${config?.baseURL || ""}${config?.url || ""}`;
      console.error(
        `[API] ${String(config?.method || "get").toUpperCase()} ${url} failed:`,
        response?.status ?? error.message,
        response?.status === 404
          ? "— either this host has no such route (check the client → host line " +
            "logged by [urls], and note setupProxy.js changes need a dev-server " +
            "restart), or the route exists and has no data for these parameters. " +
            "Probe it unauthenticated: 401 means it exists, 404 means it does not."
          : "",
      );
    }

    if (error.response?.status === 401) {
      logout();
      window.location.href = "/login";
    }
    return Promise.reject(error);
  },
);

// ---------------------------------------------------------------------------
// Generic REST helpers
// ---------------------------------------------------------------------------

export const apiService = {
  get: async (endpoint) => {
    const response = await apiClient.get(endpoint);
    return response.data;
  },
  post: async (endpoint, data) => {
    const response = await apiClient.post(endpoint, data);
    return response.data;
  },
  put: async (endpoint, data) => {
    const response = await apiClient.put(endpoint, data);
    return response.data;
  },
  delete: async (endpoint) => {
    const response = await apiClient.delete(endpoint);
    return response.data;
  },
};

// ---------------------------------------------------------------------------
// Domain API functions
// ---------------------------------------------------------------------------

/**
 * Case list for the v2 (Hegic) contract.
 *
 * GET, query params, and deliberately no source_name — the token scopes the
 * request and passing one is not required. Returns the body untouched: the
 * caller already does Object.values() on `data`, which arrives as a dict keyed
 * "0","1",… rather than an array.
 *
 * See HEGIC_API.md. Note the response carries no vehicle fields or thumbnails,
 * so the vehicle/country columns have no source for this generation.
 */
const getAssessmentListV2 = async (params = {}) => {
  const { per_page_limit = 15, page_num = 1, claim_state } = params;

  const token = getAuthToken();
  if (!token) throw new Error("No authentication token available");

  const query = {
    per_page_limit: String(per_page_limit),
    page_num: String(page_num),
  };
  if (claim_state) query.claim_state = String(claim_state);

  const response = await apiClient.get("/assessment_list/all", {
    params: query,
  });
  return assertV2Ok(response.data, "assessment_list/all");
};

/**
 * Vehicle record for one case — v2 (Hegic) only.
 *
 * The reason this exists: v2's /assessment carries no vehicle fields at all, and
 * no claim_state either. This endpoint has both — make, model, num_plate,
 * odo_meter_reading, vin_reading, claim_state — plus the ANPR `result` block.
 * Without it the detail page had nothing for its vehicle card and had to report
 * an Unknown status.
 */
/**
 * Which backend a detail call is for.
 *
 * Hegic runs the same contract on two deployments: claims on hegicpp with
 * source_name hdfcergo_pp, break-in on hegicb with hdfcergo. The session is
 * the claims session in both cases — the token is accepted by either host —
 * so the difference is per CALL, not per login, and that is what this carries.
 *
 * Omitted, every call resolves the logged-in client exactly as before.
 */
const forDeployment = (deployment) => ({
  apiHost: deployment?.apiHost,
  sourceName: deployment?.sourceName || getEffectiveSourceName(),
});

export const getVehicleDetails = async (refNum, deployment) => {
  if (!refNum) throw new Error("getVehicleDetails: ref_num is required");
  // A deployment names a v2 backend outright; without one, ask the session.
  if (!deployment && getApiGeneration() !== "v2") return null;

  const { apiHost, sourceName } = forDeployment(deployment);
  const response = await apiClient.get("/vehicle", {
    apiHost,
    params: { ref_num: refNum, source_name: sourceName },
  });
  return assertV2Ok(response.data, "vehicle");
};

/**
 * One case's full detail, normalised so callers get the same shape either way.
 *
 * v2 (Hegic): GET /assessment, which needs BOTH ref_num and source_name — the
 * list does not need source_name, but this does, and omitting it returns 400.
 * Routed through apiClient so the interceptor picks the client's own host;
 * building the URL from the shared API_BASE_URL constant would send Hegic
 * traffic to the Bajaj gateway.
 *
 * legacy: POST /assessment_new, returned untouched.
 */
export const getAssessmentDetail = async (refNum, deployment) => {
  if (!refNum) throw new Error("getAssessmentDetail: ref_num is required");

  if (deployment || getApiGeneration() === "v2") {
    // Two calls in parallel: /assessment has the damage, /vehicle has the
    // vehicle and the claim_state. Neither alone is enough to render the page.
    // The vehicle call is allowed to fail without taking the page down — damage
    // without a vehicle card still beats an error screen.
    const { apiHost, sourceName } = forDeployment(deployment);
    const [assessment, vehicle] = await Promise.all([
      apiClient
        .get("/assessment", {
          apiHost,
          params: { ref_num: refNum, source_name: sourceName },
        })
        .then((r) => assertV2Ok(r.data, "assessment")),
      getVehicleDetails(refNum, deployment).catch((err) => {
        console.info("[API] vehicle details unavailable:", err.message);
        return null;
      }),
    ]);
    return toLegacyAssessment(assessment, { vehicle });
  }

  const body = new URLSearchParams({
    ref_num: refNum,
    source_name: getEffectiveSourceName(),
  });
  const response = await apiClient.post("/assessment_new", body);
  return response.data;
};

/**
 * Point-of-impact tracking for one case — v2 (Hegic) only.
 *
 * GET /poi_tracking?ref_num=&source_name=. Note the route name: earlier probing
 * tried /poi, /point_of_impact, /assessment_poi and a dozen others and concluded
 * POI was absent from this API. It is not — it is just named something none of
 * those guesses covered. See HEGIC_API.md.
 *
 * Returns the envelope untouched; the caller normalises. `data.poi_result` is a
 * JSON *string*, not an object.
 */
export const getPoiTracking = async (refNum) => {
  if (!refNum) throw new Error("getPoiTracking: ref_num is required");
  if (getApiGeneration() !== "v2") return null;

  const response = await apiClient.get("/poi_tracking", {
    params: { ref_num: refNum, source_name: getEffectiveSourceName() },
  });
  return assertV2Ok(response.data, "poi_tracking");
};

/**
 * Establish the legacy portal's session cookie, using the token we already hold.
 *
 * This is exactly what the old portal's login page does after authenticating:
 * GET {portal}/redirect?access_token=…, whose response carries
 * `Set-Cookie: session=…; HttpOnly; Path=/`. The cookie cannot be read or written
 * from JavaScript, but it does not need to be — the browser stores it and sends
 * it on subsequent same-origin requests.
 *
 * Same-origin is the condition. In production that holds when this SPA is served
 * from the portal domain. In development setupProxy.js makes /hegic-portal
 * same-origin as far as the browser is concerned, and the proxy passes both
 * Set-Cookie and Cookie through — verified end to end.
 *
 * If the app is ever served from a domain that is not the portal's, this cannot
 * work: the cookie has no SameSite attribute, so it defaults to Lax and will not
 * be sent cross-site. That would need a server-side proxy holding the session.
 */
export const establishPortalSession = async () => {
  const origin = getPortalOrigin();
  if (origin === null) return false;

  const token = getAuthToken();
  if (!token) return false;

  const profile = getUserProfile() || {};
  const query = new URLSearchParams({
    access_token: token,
    access_level: profile.access_level ?? "",
    email: profile.email ?? "",
    permissions: (() => {
      try {
        return localStorage.getItem("permissions") || "";
      } catch {
        return "";
      }
    })(),
  });

  try {
    // The 302 to /home is expected and irrelevant — the cookie rides on it.
    await fetch(`${origin}/redirect?${query}`, {
      method: "GET",
      credentials: "include",
    });
    return true;
  } catch (err) {
    console.info("[Portal] could not establish session:", err.message);
    return false;
  }
};

/**
 * Shared plumbing for the two legacy-portal passport endpoints — same origin,
 * same session-cookie auth, same failure shape.
 *
 * Self-healing: on "not logged in" it mints a session from the token we hold and
 * retries once. That covers both the first call of a session and an expired
 * cookie, so no cookie ever has to be pasted in by hand.
 */
const postToPortal = async (endpoint, payload, { retry = true } = {}) => {
  const origin = getPortalOrigin();
  if (origin === null) return null;

  const response = await fetch(`${origin}${endpoint}`, {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      "X-Requested-With": "XMLHttpRequest",
    },
    body: JSON.stringify(payload),
  });

  const body = await response.json().catch(() => null);
  const notLoggedIn = response.status === 401 || body?.error === "not logged in";

  if (notLoggedIn && retry) {
    const established = await establishPortalSession();
    if (established) {
      return postToPortal(endpoint, payload, { retry: false });
    }
  }

  if (notLoggedIn) {
    const error = new Error("not logged in");
    error.notLoggedIn = true;
    throw error;
  }
  if (!response.ok) throw new Error(`${endpoint}: HTTP ${response.status}`);
  return body;
};

/**
 * Digital passport — the vehicle's prior history: whether it has been through
 * inspection before, and what was damaged then.
 *
 * Unlike every other call here this one goes to the client's LEGACY PORTAL
 * origin, not its API host, and authenticates with that portal's session cookie
 * rather than the Bearer token. Verified: the portal rejects the Bearer token
 * ("not logged in"), and the API host 404s on /digital_passport/query,
 * /digital_passport and /assessment/digital_passport/query.
 *
 * So `credentials: "include"` and plain fetch, deliberately — apiClient would
 * attach the wrong host and the wrong Authorization header. Same-origin in
 * production (SPA served from the portal domain); via /hegic-portal in dev.
 *
 * search_param selects the lookup mode; 2 is what the portal sends when
 * searching by VIN and plate.
 */
export const getDigitalPassport = async ({
  refNum,
  vin,
  numPlate,
  searchParam = 2,
}) =>
  postToPortal("/digital_passport/query", {
    ref_num: refNum,
    source_name: getEffectiveSourceName(),
    search_param: searchParam,
    vin: vin || "",
    num_plate: numPlate || "",
  });

/**
 * Compare one prior case against the case being viewed.
 *
 * The portal caps a comparison at three cases; that limit is enforced in the UI
 * (DigitalPassport.js) rather than here, so the caller decides what to do about
 * it rather than having a request silently dropped.
 */
export const getDigitalPassportReport = async ({
  refNum,
  sourceName,
  currentRefNum,
}) =>
  postToPortal("/digital_passport/report", {
    source_name: sourceName || getEffectiveSourceName(),
    ref_num: refNum,
    current_ref_num: currentRefNum,
    current_source_name: getEffectiveSourceName(),
  });

/**
 * One property claim — Ergo's property line only.
 *
 * POST /vehicle, form-encoded, on the property deployment. Same route name as
 * Hegic's vehicle call and nothing like it: that one is a GET with query params
 * on the v2 contract, this one is the legacy contract and returns the property
 * in `property_data` with the vehicle fields left null.
 */
export const getPropertyDetail = async (refNum) => {
  if (!refNum) throw new Error("getPropertyDetail: ref_num is required");

  const response = await apiClient.post(
    "/vehicle",
    new URLSearchParams({
      ref_num: refNum,
      source_name: getEffectiveSourceName(),
    }).toString(),
    {
      headers: {
        "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
      },
    },
  );
  return response.data;
};

/**
 * Property summary — Ergo's property line.
 *
 * Two calls on the property deployment itself, so the Bearer token, the host
 * and the 401 handling all come from apiClient: POST /image_summary asks the
 * backend to run it, GET /get_summary is polled until the result exists.
 *
 * This replaced calling the Property Insights service directly. That needed the
 * browser to read every photograph's bytes and re-upload them as multipart,
 * across a host that sends no CORS headers, to a plain-HTTP internal address —
 * three separate reasons it could not work from a browser. The backend already
 * holds the images, so a ref_num is the whole request.
 */
export const triggerPropertySummary = async (refNum) => {
  if (!refNum) throw new Error("triggerPropertySummary: ref_num is required");

  const response = await apiClient.post(
    "/image_summary",
    new URLSearchParams({
      ref_num: refNum,
      source_name: getEffectiveSourceName(),
    }).toString(),
    {
      headers: {
        "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
      },
    },
  );
  return response.data;
};

export const fetchPropertySummary = async (refNum) => {
  if (!refNum) throw new Error("fetchPropertySummary: ref_num is required");

  const response = await apiClient.get("/get_summary", {
    params: { ref_num: refNum, source_name: getEffectiveSourceName() },
  });
  return response.data;
};

/**
 * Analytics for a date range — both Ergo lines.
 *
 * The host is resolved by getAnalyticsBaseUrl, NOT by the client's usual API
 * host: motor's analytics answers on a sibling deployment. Assuming one host
 * per client is what made this route 404 in the first place.
 *
 * The default limit is high on purpose: every per-row figure the dashboard
 * derives — warnings, panels, durations — covers only the rows returned, so a
 * small page would quietly under-report rather than fail.
 */
export const getAnalyticsData = async ({
  startDate,
  stopDate,
  pageNum = 1,
  perPageLimit = 1000,
} = {}) => {
  const response = await apiClient.get("/get_analytics_data", {
    apiHost: getAnalyticsBaseUrl(),
    params: {
      source_name: getEffectiveSourceName(),
      start_date: startDate,
      stop_date: stopDate,
      per_page_limit: perPageLimit,
      page_num: pageNum,
    },
  });
  return response.data;
};

/**
 * The images the user photographed, for a v2 client.
 *
 * A separate route from the assessment on purpose: /assessment exposes only
 * the ROI set (the crops the model worked on), while this is the originals —
 * 29 of them for the case probed, against a handful of ROI images. It is also
 * far cheaper, which is why the case list uses it for thumbnails.
 */
export const getUploads = async (refNum, deployment) => {
  if (!refNum) throw new Error("getUploads: ref_num is required");

  const { apiHost, sourceName } = forDeployment(deployment);
  const response = await apiClient.get("/uploads", {
    apiHost,
    params: { ref_num: refNum, source_name: sourceName },
  });
  return toUploads(response.data);
};

export const getAssessmentList = async (params = {}) => {
  if (getApiGeneration() === "v2") return getAssessmentListV2(params);

  const {
    source_name = getEffectiveSourceName(),
    start_date,
    stop_date,
    per_page_limit = 15,
    // The country filter is carried by country_id ("latvia"), not
    // countryname — confirmed against a live portal request, which sends
    // country_id populated and countryname empty. countryname is still sent
    // (empty) because the backend expects the field to be present.
    country_id = "",
    countryname = "",
    page_num = 1,
    warning_id,
    claim_state,
  } = params;

  const token = getAuthToken();
  if (!token) throw new Error("No authentication token available");

  console.log("[API] assessment_list/all | source:", source_name, "page:", page_num);

  const requestParams = {
    source_name,
    per_page_limit: per_page_limit.toString(),
    country_id,
    countryname,
    page_num: page_num.toString(),
  };
  if (start_date) requestParams.start_date = start_date;
  if (stop_date) requestParams.stop_date = stop_date;
  if (warning_id) requestParams.warning_id = warning_id;
  if (claim_state) requestParams.claim_state = claim_state;

  const response = await apiClient.post(
    "/assessment_list/all",
    new URLSearchParams(requestParams),
  );
  return response.data;
};

export const searchCases = async (params = {}) => {
  // v2 searches by GET with a query param; POSTing returns 405. It answers with
  // a BARE dict keyed "0","1",… — no `data` wrapper, unlike the list endpoint —
  // and each row carries source_name and vehicle_type_id that the list omits.
  // Returned as-is: callers already fall back to Object.values() for a plain
  // object, so no wrapper is invented here.
  if (getApiGeneration() === "v2") {
    const { ref_num } = params;
    if (!ref_num) return null;
    const response = await apiClient.get("/search", {
      params: { ref_num },
    });
    return assertV2Ok(response.data, "search");
  }

  const {
    ref_num = "mot",
    source_name = getEffectiveSourceName(),
    country_id = "",
    countryname = "",
  } = params;

  const token = getAuthToken();
  if (!token) throw new Error("No authentication token available");

  console.log("[API] search | source:", source_name, "ref_num:", ref_num);

  // Use apiClient so the Phase 4 dynamic baseURL interceptor applies
  const response = await apiClient.post(
    "/search",
    new URLSearchParams({ ref_num, source_name, country_id, countryname }),
    {
      headers: {
        accept: "*/*",
        "accept-language": "en-US,en;q=0.9",
        authorization: `Bearer ${token}`,
        "content-type": "application/x-www-form-urlencoded; charset=UTF-8",
        origin: PORTAL_BASE_URL,
        referer: withTrailingSlash(PORTAL_BASE_URL),
        "sec-ch-ua":
          '"Chromium";v="140", "Not=A?Brand";v="24", "Google Chrome";v="140"',
        "sec-ch-ua-mobile": "?0",
        "sec-ch-ua-platform": '"macOS"',
        "sec-fetch-dest": "empty",
        "sec-fetch-mode": "cors",
        "sec-fetch-site": "same-site",
        "user-agent":
          "Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/140.0.0.0 Safari/537.36",
      },
    },
  );
  return response.data;
};

export const getAssessmentReport = async (params = {}) => {
  // v2 serves this as a GET; POSTing it returns 405 Method Not Allowed, which
  // left the stats header empty on Hegic.
  if (getApiGeneration() === "v2") {
    const { start_date, stop_date } = params;
    const query = { source_name: getEffectiveSourceName() };
    if (start_date) query.start_date = start_date;
    if (stop_date) query.stop_date = stop_date;

    const response = await apiClient.get("/assessment_report", {
      params: query,
    });
    return assertV2Ok(response.data, "assessment_report");
  }

  const defaultParams = {
    source_name: getEffectiveSourceName(),
    start_date: "2025-06-15",
    stop_date: new Date().toISOString().split("T")[0],
    country_id: "",
    countryname: "",
  };

  const queryParams = { ...defaultParams, ...params };
  if (!queryParams.country_id) delete queryParams.country_id;
  if (!queryParams.countryname) delete queryParams.countryname;

  console.log("[API] assessment_report | source:", queryParams.source_name);

  const response = await apiClient.post(
    "/assessment_report",
    new URLSearchParams(queryParams),
  );
  return response.data;
};

// Convenience wrappers kept for backward compat
export const getCases = () => apiService.get("/cases");
export const getCaseById = (id) => apiService.get(`/cases/${id}`);
export const createCase = (caseData) => apiService.post("/cases", caseData);
export const updateCase = (id, caseData) =>
  apiService.put(`/cases/${id}`, caseData);
export const deleteCase = (id) => apiService.delete(`/cases/${id}`);

export default apiService;
